import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const propertyModelErrorTypeValues = [
  "UNKNOWN",
  "CONVERSION_FAILED",
  "STRUCTURE_GEOMETRY_NOT_FOUND",
  "PROPERTY_MODEL_NOT_FOUND",
  "PROPERTY_MODEL_REQUIRED_BUT_MISSING",
  "ELEMENT_VARIANT_FIELD_MISSING",
  "PIN_ANCHOR_PARENT_MISMATCH",
  "CAUSE_COUNTS_NOT_ALLOWED",
  "PIN_PARENT_NOT_FOUND",
  "GEOMETRY_VALIDATION_FAILED",
  "ALL_STRUCTURES_INVALID",
  "BULK_ELEMENT_REJECTED",
  "ANNOTATION_FILE_NOT_ACCESSIBLE",
  "STRUCTURE_PINS_UNRESOLVED",
  "SEED_FIXTURE_FORBIDDEN_IN_PRODUCTION",
  "DAMAGEABLES_SPAN_MULTIPLE_PROPERTY_MODELS",
  "STRUCTURE_GEOMETRY_INVALID",
] as const;
const propertyModelErrorTypeSchema = z.enum(propertyModelErrorTypeValues);
const PropertyModelErrorType = propertyModelErrorTypeSchema.enum;
type PropertyModelErrorType = z.infer<typeof propertyModelErrorTypeSchema>;

export type PropertyModelErrors =
  | { errorType: typeof PropertyModelErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof PropertyModelErrorType.CONVERSION_FAILED; data: { source: string; reason: string } }
  | { errorType: typeof PropertyModelErrorType.STRUCTURE_GEOMETRY_NOT_FOUND; data: { structureId: string } }
  | { errorType: typeof PropertyModelErrorType.PROPERTY_MODEL_NOT_FOUND; data: { propertyAddressId: string } }
  | { errorType: typeof PropertyModelErrorType.PROPERTY_MODEL_REQUIRED_BUT_MISSING; data: { projectId: number } }
  | {
      errorType: typeof PropertyModelErrorType.ELEMENT_VARIANT_FIELD_MISSING;
      data: { elementId: string; type: string; missingField: string };
    }
  | {
      errorType: typeof PropertyModelErrorType.PIN_ANCHOR_PARENT_MISMATCH;
      data: { id: string; parentKind: string; parentClass: string };
    }
  | {
      errorType: typeof PropertyModelErrorType.CAUSE_COUNTS_NOT_ALLOWED;
      data: { id: string; reason: "NOT_A_TEST_SQUARE" | "CAUSE_NOT_LISTED" };
    }
  | { errorType: typeof PropertyModelErrorType.PIN_PARENT_NOT_FOUND; data: { id: string } }
  | { errorType: typeof PropertyModelErrorType.SEED_FIXTURE_FORBIDDEN_IN_PRODUCTION; data: { fixture: string } }
  | {
      errorType: typeof PropertyModelErrorType.STRUCTURE_PINS_UNRESOLVED;
      data: { structureId: string; projectedPins: number; drawnPins: number };
    }
  | { errorType: typeof PropertyModelErrorType.BULK_ELEMENT_REJECTED; data: { reason: string } }
  | {
      errorType: typeof PropertyModelErrorType.GEOMETRY_VALIDATION_FAILED;
      data: {
        propertyAddressId: string;
        structureIndex?: number;
        descriptions: string[];
        faceIds: string[];
        totalDescriptions: number;
        totalFaceIds: number;
        invalidFaceSourceIds: string[];
        hasUnattributableError: boolean;
      };
    }
  | {
      errorType: typeof PropertyModelErrorType.ALL_STRUCTURES_INVALID;
      data: { propertyAddressId: string; provider: string; skippedStructureIndexes: number[] };
    }
  | {
      errorType: typeof PropertyModelErrorType.ANNOTATION_FILE_NOT_ACCESSIBLE;
      data: { elementClass: string; elementId: string; fileIds: string[] };
    }
  | {
      errorType: typeof PropertyModelErrorType.DAMAGEABLES_SPAN_MULTIPLE_PROPERTY_MODELS;
      data: { estimateId: string; propertyModelIds: string[] };
    }
  | {
      errorType: typeof PropertyModelErrorType.STRUCTURE_GEOMETRY_INVALID;
      data: {
        propertyAddressId: string;
        invalidFaces: { sourceId: string; code: number; description: string }[];
      };
    };

export const propertyModelErrorDetails: Record<PropertyModelErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown property model error" },
  CONVERSION_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Failed to convert stored CityJSON geometry to a GLB",
  },
  STRUCTURE_GEOMETRY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No stored geometry found for the requested structure",
  },
  STRUCTURE_PINS_UNRESOLVED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Scope packet dropped a projected pin whose id is not on the structure",
  },
  PROPERTY_MODEL_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No ingested property model found for the requested property address",
  },
  PROPERTY_MODEL_REQUIRED_BUT_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A property model is required for this document but none was found",
  },
  ELEMENT_VARIANT_FIELD_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Element type change omitted a field the target variant requires",
  },
  PIN_ANCHOR_PARENT_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Pin anchor components do not match the resolved parent element class",
  },
  CAUSE_COUNTS_NOT_ALLOWED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Damage cause counts sent on an element other than a test square pin, or for an unlisted cause",
  },
  BULK_ELEMENT_REJECTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Bulk property-model element rejected by per-element validation",
  },
  PIN_PARENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Pin parent element could not be resolved",
  },
  SEED_FIXTURE_FORBIDDEN_IN_PRODUCTION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A property-model seed fixture was requested against production",
  },
  GEOMETRY_VALIDATION_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Ingested CityJSON geometry failed validation",
  },
  ALL_STRUCTURES_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Every structure on an ingested property failed geometry validation, so none persisted",
  },
  ANNOTATION_FILE_NOT_ACCESSIBLE: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Annotation attachment references a file the caller cannot read",
  },
  DAMAGEABLES_SPAN_MULTIPLE_PROPERTY_MODELS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "An estimate's damageables resolve to more than one property model",
  },
  STRUCTURE_GEOMETRY_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Posted structure geometry failed val3dity validation",
  },
};
